import React, { useEffect, useMemo, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import UserPreferences from "@/components/preferences/UserPreferences";

const DRAFT_KEY = "settings_draft_v1";
const PUBLISHED_KEY = "settings_published_v1";

type GeneralSettings = {
  locale: string;
  dateFormat: string;
  currency: string;
  decimalSeparator: "," | ".";
  timeZone: string;
  numberingPattern: string;
  primaryColor: string;
  themeMode: "light" | "dark" | "system";
  businessDays: string[]; // ["mon","tue",...]
  holidaysNote: string;
  logo?: string; // data URL
  favicon?: string; // data URL
};

type NotificationsSettings = {
  channels: { app: boolean; email: boolean; whatsapp: boolean };
  events: Record<string, { app: boolean; email: boolean; whatsapp: boolean }>;
  digest: { enabled: boolean; frequency: "daily" | "weekly"; time: string };
};

type IntegrationsSettings = {
  smtp: { host: string; port: number; secure: "TLS" | "SSL" | "Nenhum"; user: string; from: string };
  whatsapp: { provider: string; token: string; fromNumber: string };
  push: { provider: string; appId: string; key: string };
  storage: { provider: string; bucket: string; accessKey: string; secretKey: string };
  weather: { provider: string; apiKey: string };
  webhooks: { apiKey: string; scopes: string; callbackUrls: string };
};

type AISettings = {
  enabled: boolean;
  provider: "OpenAI";
  apiKey: string;
  temperature: number;
  maxTokens: number;
  systemPrompt: string;
  noSensitiveData: boolean;
  suggestions: { subtasks: boolean; prioritization: boolean; redistribution: boolean; notificationTemplates: boolean };
  logPrompts: boolean;
};

type RDOSettings = {
  statusesCsv: string; // comma separated
  fields: {
    clima: boolean; praticavel: boolean; maoDeObra: boolean; equipamentos: boolean; atividades: boolean; materiais: boolean; comentarios: boolean; midia: boolean; anexos: boolean; assinatura: boolean;
  };
  assinatura: { tipo: "desenho" | "certificado"; obrigatoria: boolean };
  captureCloseDate: boolean;
  workflow: string;
};

type TasksSettings = {
  statusesCsv: string;
  fields: { prioridade: boolean; tags: boolean; prazo: boolean; multiResponsavel: boolean; anexos: boolean };
  slaHoras: { baixa: number; media: number; alta: number };
  kanbanColumnsCsv: string;
  ganttUnit: "semana" | "mes";
  ai: { nlCreate: boolean; autoSubtasks: boolean };
};

type CatalogsSettings = {
  materiais: { enableSystemImport: boolean; umsPadrao: string; categorias: string };
  equipamentos: { tipos: string; manutencaoPeriodicidadeDias: number };
  setores: { cores: string; codigos: string; iconesHint: string };
  parceiros: { fornecedoresCamposMinimos: string; clientesCamposMinimos: string };
};

type ReportsSettings = {
  defaultKpis: string;
  layoutsPreferidos: string;
  tooltips: { mostrarLegendaAoHover: boolean; mostrarValorAoHover: boolean };
  branding: { logo?: string; contato: string };
};

type SecuritySettings = {
  senha: { tamanhoMin: number; forca: "baixa" | "media" | "alta" };
  mfaObrigatorioPerfis: string;
  sso: boolean;
  ipRanges: string;
  sessionTimeoutMin: number;
  auditoria: boolean;
  backup: { frequencia: "diario" | "semanal" | "mensal"; retencaoDias: number };
};

type Settings = {
  general: GeneralSettings;
  notifications: NotificationsSettings;
  integrations: IntegrationsSettings;
  ai: AISettings;
  rdo: RDOSettings;
  tasks: TasksSettings;
  catalogs: CatalogsSettings;
  reports: ReportsSettings;
  security: SecuritySettings;
};

const defaultSettings: Settings = {
  general: {
    locale: "pt-BR",
    dateFormat: "DD/MM/AAAA",
    currency: "BRL",
    decimalSeparator: ",",
    timeZone: "America/Sao_Paulo",
    numberingPattern: "{OBRA}-{AAAA}-{####}",
    primaryColor: "#0071CE",
    themeMode: "system",
    businessDays: ["mon", "tue", "wed", "thu", "fri"],
    holidaysNote: "Usar calendário Brasil + exceções da empresa",
  },
  notifications: {
    channels: { app: true, email: false, whatsapp: false },
    events: {
      novaTarefa: { app: true, email: false, whatsapp: false },
      statusAlterado: { app: true, email: false, whatsapp: false },
      tarefaAtrasada: { app: true, email: false, whatsapp: false },
      novoComentario: { app: true, email: false, whatsapp: false },
      ocorrencia: { app: true, email: false, whatsapp: false },
      rdoAguardando: { app: true, email: false, whatsapp: false },
      rdoAprovado: { app: true, email: false, whatsapp: false },
      rdoReprovado: { app: true, email: false, whatsapp: false },
    },
    digest: { enabled: false, frequency: "daily", time: "08:00" },
  },
  integrations: {
    smtp: { host: "", port: 587, secure: "TLS", user: "", from: "" },
    whatsapp: { provider: "", token: "", fromNumber: "" },
    push: { provider: "", appId: "", key: "" },
    storage: { provider: "", bucket: "", accessKey: "", secretKey: "" },
    weather: { provider: "", apiKey: "" },
    webhooks: { apiKey: "", scopes: "", callbackUrls: "" },
  },
  ai: {
    enabled: false,
    provider: "OpenAI",
    apiKey: "",
    temperature: 0.3,
    maxTokens: 1000,
    systemPrompt: "Você é um assistente que ajuda na gestão de obras.",
    noSensitiveData: true,
    suggestions: { subtasks: true, prioritization: true, redistribution: false, notificationTemplates: true },
    logPrompts: false,
  },
  rdo: {
    statusesCsv: "Relatório sendo preenchido, Revisar relatório, Aprovado",
    fields: { clima: true, praticavel: true, maoDeObra: true, equipamentos: true, atividades: true, materiais: true, comentarios: true, midia: true, anexos: true, assinatura: true },
    assinatura: { tipo: "desenho", obrigatoria: false },
    captureCloseDate: true,
    workflow: "Nível 1: Engenheiro → Nível 2: Diretor",
  },
  tasks: {
    statusesCsv: "Não iniciado, Em execução, Aguardando, Concluído, Declinado",
    fields: { prioridade: true, tags: true, prazo: true, multiResponsavel: false, anexos: true },
    slaHoras: { baixa: 72, media: 48, alta: 24 },
    kanbanColumnsCsv: "Backlog, Em andamento, Em revisão, Concluído",
    ganttUnit: "semana",
    ai: { nlCreate: true, autoSubtasks: true },
  },
  catalogs: {
    materiais: { enableSystemImport: true, umsPadrao: "un, kg, m", categorias: "Estrutura; Acabamentos" },
    equipamentos: { tipos: "próprio, locado", manutencaoPeriodicidadeDias: 30 },
    setores: { cores: "#0071CE; #00B894", codigos: "CIV; ELC; HID", iconesHint: "Configurar em Cadastro > Setores" },
    parceiros: { fornecedoresCamposMinimos: "CNPJ, Razão Social", clientesCamposMinimos: "CNPJ/CPF, Nome" },
  },
  reports: {
    defaultKpis: "Progresso físico; Tarefas atrasadas; Custos",
    layoutsPreferidos: "heatmap, radar, área empilhada",
    tooltips: { mostrarLegendaAoHover: true, mostrarValorAoHover: true },
    branding: { contato: "contato@empresa.com" },
  },
  security: {
    senha: { tamanhoMin: 8, forca: "media" },
    mfaObrigatorioPerfis: "Diretor; Gestor",
    sso: false,
    ipRanges: "",
    sessionTimeoutMin: 60,
    auditoria: true,
    backup: { frequencia: "semanal", retencaoDias: 30 },
  },
};

function useSettings() {
  const [draft, setDraft] = useState<Settings>(() => {
    const local = localStorage.getItem(DRAFT_KEY);
    if (local) return JSON.parse(local);
    const published = localStorage.getItem(PUBLISHED_KEY);
    return published ? JSON.parse(published) : defaultSettings;
  });
  const published = useMemo<Settings | null>(() => {
    const p = localStorage.getItem(PUBLISHED_KEY);
    return p ? JSON.parse(p) : null;
  }, []);

  const saveDraft = () => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    toast.success("Rascunho salvo.");
  };
  const publish = () => {
    localStorage.setItem(PUBLISHED_KEY, JSON.stringify(draft));
    toast.success("Alterações publicadas.");
  };
  const revert = () => {
    const p = localStorage.getItem(PUBLISHED_KEY);
    if (p) {
      const parsed = JSON.parse(p);
      setDraft(parsed);
      localStorage.setItem(DRAFT_KEY, JSON.stringify(parsed));
      toast.info("Rascunho revertido para a última publicação.");
    } else {
      setDraft(defaultSettings);
      localStorage.setItem(DRAFT_KEY, JSON.stringify(defaultSettings));
      toast.info("Sem publicação anterior. Rascunho resetado ao padrão.");
    }
  };
  const exportJson = () => {
    const blob = new Blob([JSON.stringify({ draft, published }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "configuracoes.json";
    a.click();
    URL.revokeObjectURL(url);
  };
  const importJson = async (file: File) => {
    const text = await file.text();
    const data = JSON.parse(text);
    if (data?.draft) {
      setDraft(data.draft);
      localStorage.setItem(DRAFT_KEY, JSON.stringify(data.draft));
    }
    if (data?.published) {
      localStorage.setItem(PUBLISHED_KEY, JSON.stringify(data.published));
    }
    toast.success("Configurações importadas.");
  };

  return { draft, setDraft, saveDraft, publish, revert, exportJson, importJson };
}

function FieldRow({ label, children, description }: { label: string; children: React.ReactNode; description?: string }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center py-2">
      <div>
        <Label className="text-foreground">{label}</Label>
        {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
      </div>
      <div className="md:col-span-2">{children}</div>
    </div>
  );
}

function FilePicker({ onPick }: { onPick: (dataUrl: string) => void }) {
  return (
    <Input
      type="file"
      accept="image/*"
      onChange={async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const reader = new FileReader();
        reader.onload = () => onPick(String(reader.result));
        reader.readAsDataURL(f);
      }}
    />
  );
}

export default function Configuracoes() {
  // Workspace single-tenant: o usuário autenticado é o dono/admin dos próprios
  // dados (papéis por obra virão de work_users no modo multiusuário). A antiga
  // flag "app_is_admin" do localStorage foi removida na Fase 7.
  const isAdmin = true;

  useEffect(() => {
    document.title = isAdmin ? "Configurações do Sistema" : "Minhas Preferências";
  }, [isAdmin]);

  // Hooks SEMPRE antes de qualquer return condicional (rules-of-hooks): se o
  // branch !isAdmin voltar a existir, a ordem dos hooks não pode variar.
  const { draft, setDraft, saveDraft, publish, revert, exportJson, importJson } = useSettings();

  const numberingPreview = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const seq = "0001";
    return draft.general.numberingPattern
      .replace(/\{OBRA\}/g, "OBRA123")
      .replace(/\{AAAA\}/g, String(year))
      .replace(/\{####\}/g, seq);
  }, [draft.general.numberingPattern]);

  if (!isAdmin) {
    return <UserPreferences />;
  }

  return (
    <div className="p-6 space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Configurações do Sistema</h1>
          <p className="text-sm text-muted-foreground">Gerencie padrões globais e integrações.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">Ambiente: Sandbox</Badge>
          <Separator orientation="vertical" className="hidden h-6 sm:block" />
          <Button variant="default" onClick={saveDraft}>Salvar</Button>
          <Button variant="secondary" onClick={saveDraft}>Salvar como rascunho</Button>
          <Button variant="outline" onClick={publish}>Publicar alterações</Button>
          <Button variant="destructive" onClick={revert}>Reverter mudanças</Button>
        </div>
      </header>

      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={exportJson}>Exportar JSON</Button>
        <label className="inline-flex items-center gap-2">
          <Input type="file" accept="application/json" onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) importJson(f);
          }} />
        </label>
      </div>

      <Tabs defaultValue="geral" className="w-full">
        <TabsList className="flex h-auto w-full max-w-full justify-start gap-1 overflow-x-auto">
          <TabsTrigger className="h-11 shrink-0 sm:h-9" value="geral">Geral</TabsTrigger>
          <TabsTrigger className="h-11 shrink-0 sm:h-9" value="usuarios">Usuários & Permissões</TabsTrigger>
          <TabsTrigger className="h-11 shrink-0 sm:h-9" value="notificacoes">Notificações</TabsTrigger>
          <TabsTrigger className="h-11 shrink-0 sm:h-9" value="integracoes">Integrações</TabsTrigger>
          <TabsTrigger className="h-11 shrink-0 sm:h-9" value="ia">IA / Assistente</TabsTrigger>
          <TabsTrigger className="h-11 shrink-0 sm:h-9" value="rdo">Diário de Obra (RDO)</TabsTrigger>
          <TabsTrigger className="h-11 shrink-0 sm:h-9" value="tarefas">Tarefas / Kanban / Cronograma</TabsTrigger>
          <TabsTrigger className="h-11 shrink-0 sm:h-9" value="catalogos">Catálogos Centrais</TabsTrigger>
          <TabsTrigger className="h-11 shrink-0 sm:h-9" value="relatorios">Relatórios & Dashboards</TabsTrigger>
          <TabsTrigger className="h-11 shrink-0 sm:h-9" value="campos">Campos Personalizados</TabsTrigger>
          <TabsTrigger className="h-11 shrink-0 sm:h-9" value="seguranca">Segurança & Auditoria</TabsTrigger>
        </TabsList>

        {/* Geral */}
        <TabsContent value="geral">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Localidade & Formatos</CardTitle>
                <CardDescription>Idiomas, datas, moeda e fuso horário.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <FieldRow label="Idioma e localidade">
                  <Select value={draft.general.locale} onValueChange={(v) => setDraft({ ...draft, general: { ...draft.general, locale: v } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pt-BR">Português (Brasil)</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldRow>
                <FieldRow label="Formato de data">
                  <Input value={draft.general.dateFormat} onChange={(e) => setDraft({ ...draft, general: { ...draft.general, dateFormat: e.target.value } })} />
                </FieldRow>
                <FieldRow label="Moeda">
                  <Select value={draft.general.currency} onValueChange={(v) => setDraft({ ...draft, general: { ...draft.general, currency: v } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BRL">BRL</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldRow>
                <FieldRow label="Separador decimal">
                  <Select value={draft.general.decimalSeparator} onValueChange={(v) => setDraft({ ...draft, general: { ...draft.general, decimalSeparator: v as any } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value=",">Vírgula</SelectItem>
                      <SelectItem value=".">Ponto</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldRow>
                <FieldRow label="Fuso horário padrão">
                  <Select value={draft.general.timeZone} onValueChange={(v) => setDraft({ ...draft, general: { ...draft.general, timeZone: v } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="America/Sao_Paulo">America/Sao_Paulo</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldRow>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Numeração automática</CardTitle>
                <CardDescription>Padrões para códigos de Obras e RDO.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <FieldRow label="Padrão">
                  <Input value={draft.general.numberingPattern} onChange={(e) => setDraft({ ...draft, general: { ...draft.general, numberingPattern: e.target.value } })} />
                </FieldRow>
                <div className="text-sm text-muted-foreground">Prévia: <span className="text-foreground font-medium">{numberingPreview}</span></div>
                <Separator />
                <FieldRow label="Dias úteis">
                  <div className="flex flex-wrap gap-2">
                    {[{k:"mon",l:"Seg"},{k:"tue",l:"Ter"},{k:"wed",l:"Qua"},{k:"thu",l:"Qui"},{k:"fri",l:"Sex"},{k:"sat",l:"Sáb"},{k:"sun",l:"Dom"}].map(d => (
                      <Button key={d.k} type="button" variant={draft.general.businessDays.includes(d.k) ? "default" : "outline"} size="sm" onClick={() => {
                        const set = new Set(draft.general.businessDays);
                        set.has(d.k) ? set.delete(d.k) : set.add(d.k);
                        setDraft({ ...draft, general: { ...draft.general, businessDays: Array.from(set) } });
                      }}>{d.l}</Button>
                    ))}
                  </div>
                </FieldRow>
                <FieldRow label="Feriados e exceções">
                  <Textarea value={draft.general.holidaysNote} onChange={(e) => setDraft({ ...draft, general: { ...draft.general, holidaysNote: e.target.value } })} placeholder="Descrição ou lista de feriados" />
                </FieldRow>
              </CardContent>
            </Card>

            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Tema / Aparência</CardTitle>
                <CardDescription>Logotipo, favicon, cor primária e modo.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <FieldRow label="Cor primária">
                  <div className="flex items-center gap-3">
                    <Input type="color" value={draft.general.primaryColor} onChange={(e) => setDraft({ ...draft, general: { ...draft.general, primaryColor: e.target.value } })} className="h-10 w-20 p-1" />
                    <span className="text-sm text-muted-foreground">{draft.general.primaryColor}</span>
                  </div>
                </FieldRow>
                <FieldRow label="Modo do tema">
                  <Select value={draft.general.themeMode} onValueChange={(v) => setDraft({ ...draft, general: { ...draft.general, themeMode: v as any } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">Claro</SelectItem>
                      <SelectItem value="dark">Escuro</SelectItem>
                      <SelectItem value="system">Sistema</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldRow>
                <FieldRow label="Logotipo">
                  <div className="flex items-center gap-4">
                    <FilePicker onPick={(data) => setDraft({ ...draft, general: { ...draft.general, logo: data } })} />
                    {draft.general.logo && <img src={draft.general.logo} alt="Logotipo" className="h-10 rounded" />}
                  </div>
                </FieldRow>
                <FieldRow label="Favicon">
                  <div className="flex items-center gap-4">
                    <FilePicker onPick={(data) => setDraft({ ...draft, general: { ...draft.general, favicon: data } })} />
                    {draft.general.favicon && <img src={draft.general.favicon} alt="Favicon" className="h-6 rounded" />}
                  </div>
                </FieldRow>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Usuários & Permissões */}
        <TabsContent value="usuarios">
          <Card>
            <CardHeader>
              <CardTitle>Perfis padrão</CardTitle>
              <CardDescription>Defina os perfis e permissões base.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-muted-foreground">Diretor, Gestor, Engenheiro, Mestre, Administrativo, Cliente/Visitante</div>
              <div className="overflow-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left">
                      <th className="py-2 pr-2">Módulo</th>
                      {["Visualizar","Criar","Editar","Aprovar","Excluir","Exportar","Configurar"].map(h => <th key={h} className="py-2 px-2">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {["Obras","Tarefas","Relatórios","Configurações"].map(mod => (
                      <tr key={mod} className="border-t border-border">
                        <td className="py-2 pr-2">{mod}</td>
                        {Array.from({ length: 7 }).map((_, i) => (
                          <td key={i} className="py-2 px-2"><Switch /></td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2"><Switch id="herdar" /><Label htmlFor="herdar">Herdar para novos usuários</Label></div>
                <Button variant="outline" onClick={() => toast.message("Aplicado às permissões atuais (simulado).")}>Aplicar a atuais</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notificações */}
        <TabsContent value="notificacoes">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Canais</CardTitle>
                <CardDescription>Ative os canais de envio.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {[{k:"app",l:"App (push)"},{k:"email",l:"E-mail"},{k:"whatsapp",l:"WhatsApp"}].map(c => (
                  <div key={c.k} className="flex items-center justify-between">
                    <span>{c.l}</span>
                    <Switch checked={(draft.notifications.channels as any)[c.k]} onCheckedChange={(v) => setDraft({ ...draft, notifications: { ...draft.notifications, channels: { ...draft.notifications.channels, [c.k]: v } } })} />
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Digest</CardTitle>
                <CardDescription>Resumo diário/semanal.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between"><span>Ativar digest</span><Switch checked={draft.notifications.digest.enabled} onCheckedChange={(v) => setDraft({ ...draft, notifications: { ...draft.notifications, digest: { ...draft.notifications.digest, enabled: v } } })} /></div>
                <FieldRow label="Frequência">
                  <Select value={draft.notifications.digest.frequency} onValueChange={(v) => setDraft({ ...draft, notifications: { ...draft.notifications, digest: { ...draft.notifications.digest, frequency: v as any } } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Diário</SelectItem>
                      <SelectItem value="weekly">Semanal</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldRow>
                <FieldRow label="Hora de envio"><Input type="time" value={draft.notifications.digest.time} onChange={(e) => setDraft({ ...draft, notifications: { ...draft.notifications, digest: { ...draft.notifications.digest, time: e.target.value } } })} /></FieldRow>
                <div className="flex gap-2"><Button variant="outline" onClick={() => toast.message("E-mail de teste enviado (simulado).")}>Enviar e-mail de teste</Button><Button variant="outline" onClick={() => toast.message("WhatsApp de teste enviado (simulado).")}>Enviar WhatsApp de teste</Button><Button variant="outline" onClick={() => toast.message("Push de teste enviado (simulado).")}>Enviar push de teste</Button></div>
              </CardContent>
            </Card>

            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Eventos</CardTitle>
                <CardDescription>Ative por canal.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(draft.notifications.events).map(([k, v]) => (
                  <div key={k} className="grid grid-cols-2 md:grid-cols-4 items-center gap-2 border-b border-border py-2">
                    <div className="capitalize">{k.replace(/([A-Z])/g, " $1").toLowerCase()}</div>
                    <div className="flex items-center gap-2 md:col-span-3">
                      <div className="flex items-center gap-2"><Switch checked={v.app} onCheckedChange={(val) => setDraft({ ...draft, notifications: { ...draft.notifications, events: { ...draft.notifications.events, [k]: { ...v, app: val } } } })} /><span>App</span></div>
                      <div className="flex items-center gap-2"><Switch checked={v.email} onCheckedChange={(val) => setDraft({ ...draft, notifications: { ...draft.notifications, events: { ...draft.notifications.events, [k]: { ...v, email: val } } } })} /><span>E-mail</span></div>
                      <div className="flex items-center gap-2"><Switch checked={v.whatsapp} onCheckedChange={(val) => setDraft({ ...draft, notifications: { ...draft.notifications, events: { ...draft.notifications.events, [k]: { ...v, whatsapp: val } } } })} /><span>WhatsApp</span></div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Integrações */}
        <TabsContent value="integracoes">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader><CardTitle>E-mail (SMTP)</CardTitle><CardDescription>Host, porta, TLS/SSL, usuário, remetente padrão.</CardDescription></CardHeader>
              <CardContent className="space-y-3">
                <FieldRow label="Host"><Input value={draft.integrations.smtp.host} onChange={(e) => setDraft({ ...draft, integrations: { ...draft.integrations, smtp: { ...draft.integrations.smtp, host: e.target.value } } })} /></FieldRow>
                <FieldRow label="Porta"><Input type="number" value={draft.integrations.smtp.port} onChange={(e) => setDraft({ ...draft, integrations: { ...draft.integrations, smtp: { ...draft.integrations.smtp, port: Number(e.target.value) } } })} /></FieldRow>
                <FieldRow label="Segurança">
                  <Select value={draft.integrations.smtp.secure} onValueChange={(v) => setDraft({ ...draft, integrations: { ...draft.integrations, smtp: { ...draft.integrations.smtp, secure: v as any } } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TLS">TLS</SelectItem>
                      <SelectItem value="SSL">SSL</SelectItem>
                      <SelectItem value="Nenhum">Nenhum</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldRow>
                <FieldRow label="Usuário"><Input value={draft.integrations.smtp.user} onChange={(e) => setDraft({ ...draft, integrations: { ...draft.integrations, smtp: { ...draft.integrations.smtp, user: e.target.value } } })} /></FieldRow>
                <FieldRow label="Remetente"><Input placeholder="nome <email@dominio>" value={draft.integrations.smtp.from} onChange={(e) => setDraft({ ...draft, integrations: { ...draft.integrations, smtp: { ...draft.integrations.smtp, from: e.target.value } } })} /></FieldRow>
                <Button variant="outline" onClick={() => toast.message("Teste SMTP enviado (simulado).")}>Testar SMTP</Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>WhatsApp (API)</CardTitle><CardDescription>Provedor, token e número remetente.</CardDescription></CardHeader>
              <CardContent className="space-y-3">
                <FieldRow label="Provedor"><Input value={draft.integrations.whatsapp.provider} onChange={(e) => setDraft({ ...draft, integrations: { ...draft.integrations, whatsapp: { ...draft.integrations.whatsapp, provider: e.target.value } } })} /></FieldRow>
                <FieldRow label="Token"><Input type="password" value={draft.integrations.whatsapp.token} onChange={(e) => setDraft({ ...draft, integrations: { ...draft.integrations, whatsapp: { ...draft.integrations.whatsapp, token: e.target.value } } })} /></FieldRow>
                <FieldRow label="Número remetente"><Input value={draft.integrations.whatsapp.fromNumber} onChange={(e) => setDraft({ ...draft, integrations: { ...draft.integrations, whatsapp: { ...draft.integrations.whatsapp, fromNumber: e.target.value } } })} /></FieldRow>
                <Button variant="outline" onClick={() => toast.message("WhatsApp de teste enviado (simulado).")}>Testar WhatsApp</Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Push</CardTitle><CardDescription>Chaves e App ID.</CardDescription></CardHeader>
              <CardContent className="space-y-3">
                <FieldRow label="Provedor"><Input value={draft.integrations.push.provider} onChange={(e) => setDraft({ ...draft, integrations: { ...draft.integrations, push: { ...draft.integrations.push, provider: e.target.value } } })} /></FieldRow>
                <FieldRow label="App ID"><Input value={draft.integrations.push.appId} onChange={(e) => setDraft({ ...draft, integrations: { ...draft.integrations, push: { ...draft.integrations.push, appId: e.target.value } } })} /></FieldRow>
                <FieldRow label="Chave"><Input value={draft.integrations.push.key} onChange={(e) => setDraft({ ...draft, integrations: { ...draft.integrations, push: { ...draft.integrations.push, key: e.target.value } } })} /></FieldRow>
                <Button variant="outline" onClick={() => toast.message("Push de teste enviado (simulado).")}>Testar Push</Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Armazenamento</CardTitle><CardDescription>S3/MinIO/Drive</CardDescription></CardHeader>
              <CardContent className="space-y-3">
                <FieldRow label="Provedor"><Input value={draft.integrations.storage.provider} onChange={(e) => setDraft({ ...draft, integrations: { ...draft.integrations, storage: { ...draft.integrations.storage, provider: e.target.value } } })} /></FieldRow>
                <FieldRow label="Bucket"><Input value={draft.integrations.storage.bucket} onChange={(e) => setDraft({ ...draft, integrations: { ...draft.integrations, storage: { ...draft.integrations.storage, bucket: e.target.value } } })} /></FieldRow>
                <FieldRow label="Access Key"><Input value={draft.integrations.storage.accessKey} onChange={(e) => setDraft({ ...draft, integrations: { ...draft.integrations, storage: { ...draft.integrations.storage, accessKey: e.target.value } } })} /></FieldRow>
                <FieldRow label="Secret Key"><Input type="password" value={draft.integrations.storage.secretKey} onChange={(e) => setDraft({ ...draft, integrations: { ...draft.integrations, storage: { ...draft.integrations.storage, secretKey: e.target.value } } })} /></FieldRow>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Clima/Condições (RDO)</CardTitle><CardDescription>Provedor e chave.</CardDescription></CardHeader>
              <CardContent className="space-y-3">
                <FieldRow label="Provedor"><Input value={draft.integrations.weather.provider} onChange={(e) => setDraft({ ...draft, integrations: { ...draft.integrations, weather: { ...draft.integrations.weather, provider: e.target.value } } })} /></FieldRow>
                <FieldRow label="Chave"><Input value={draft.integrations.weather.apiKey} onChange={(e) => setDraft({ ...draft, integrations: { ...draft.integrations, weather: { ...draft.integrations.weather, apiKey: e.target.value } } })} /></FieldRow>
              </CardContent>
            </Card>

            <Card className="md:col-span-2">
              <CardHeader><CardTitle>Webhooks & API interna</CardTitle><CardDescription>Chaves, escopos e URLs de callback.</CardDescription></CardHeader>
              <CardContent className="space-y-3">
                <FieldRow label="API Key"><Input value={draft.integrations.webhooks.apiKey} onChange={(e) => setDraft({ ...draft, integrations: { ...draft.integrations, webhooks: { ...draft.integrations.webhooks, apiKey: e.target.value } } })} /></FieldRow>
                <FieldRow label="Escopos"><Input value={draft.integrations.webhooks.scopes} onChange={(e) => setDraft({ ...draft, integrations: { ...draft.integrations, webhooks: { ...draft.integrations.webhooks, scopes: e.target.value } } })} /></FieldRow>
                <FieldRow label="URLs de callback (separadas por linha)"><Textarea value={draft.integrations.webhooks.callbackUrls} onChange={(e) => setDraft({ ...draft, integrations: { ...draft.integrations, webhooks: { ...draft.integrations.webhooks, callbackUrls: e.target.value } } })} /></FieldRow>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* IA / Assistente */}
        <TabsContent value="ia">
          <Card>
            <CardHeader><CardTitle>Assistente de IA</CardTitle><CardDescription>Parâmetros do provedor e políticas.</CardDescription></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between"><span>Ativar IA</span><Switch checked={draft.ai.enabled} onCheckedChange={(v) => setDraft({ ...draft, ai: { ...draft.ai, enabled: v } })} /></div>
              <FieldRow label="Provedor"><Input disabled value={draft.ai.provider} /></FieldRow>
              <FieldRow label="Chave da API"><Input type="password" value={draft.ai.apiKey} onChange={(e) => setDraft({ ...draft, ai: { ...draft.ai, apiKey: e.target.value } })} /></FieldRow>
              <FieldRow label="Temperatura"><Input type="number" step="0.1" value={draft.ai.temperature} onChange={(e) => setDraft({ ...draft, ai: { ...draft.ai, temperature: Number(e.target.value) } })} /></FieldRow>
              <FieldRow label="Limite de tokens"><Input type="number" value={draft.ai.maxTokens} onChange={(e) => setDraft({ ...draft, ai: { ...draft.ai, maxTokens: Number(e.target.value) } })} /></FieldRow>
              <FieldRow label="Prompt de sistema"><Textarea value={draft.ai.systemPrompt} onChange={(e) => setDraft({ ...draft, ai: { ...draft.ai, systemPrompt: e.target.value } })} /></FieldRow>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2"><Switch checked={draft.ai.noSensitiveData} onCheckedChange={(v) => setDraft({ ...draft, ai: { ...draft.ai, noSensitiveData: v } })} /><span>Não armazenar conteúdo sensível</span></div>
                <div className="flex items-center gap-2"><Switch checked={draft.ai.logPrompts} onCheckedChange={(v) => setDraft({ ...draft, ai: { ...draft.ai, logPrompts: v } })} /><span>Log de prompts</span></div>
              </div>
              <Separator />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="flex items-center gap-2"><Switch checked={draft.ai.suggestions.subtasks} onCheckedChange={(v) => setDraft({ ...draft, ai: { ...draft.ai, suggestions: { ...draft.ai.suggestions, subtasks: v } } })} /><span>Gerar subtarefas</span></div>
                <div className="flex items-center gap-2"><Switch checked={draft.ai.suggestions.prioritization} onCheckedChange={(v) => setDraft({ ...draft, ai: { ...draft.ai, suggestions: { ...draft.ai.suggestions, prioritization: v } } })} /><span>Priorização</span></div>
                <div className="flex items-center gap-2"><Switch checked={draft.ai.suggestions.redistribution} onCheckedChange={(v) => setDraft({ ...draft, ai: { ...draft.ai, suggestions: { ...draft.ai.suggestions, redistribution: v } } })} /><span>Redistribuição</span></div>
                <div className="flex items-center gap-2"><Switch checked={draft.ai.suggestions.notificationTemplates} onCheckedChange={(v) => setDraft({ ...draft, ai: { ...draft.ai, suggestions: { ...draft.ai.suggestions, notificationTemplates: v } } })} /><span>Mensagens padrão de notificações</span></div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* RDO */}
        <TabsContent value="rdo">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader><CardTitle>Status do RDO</CardTitle><CardDescription>Edite a lista e cores (simples).</CardDescription></CardHeader>
              <CardContent className="space-y-3">
                <FieldRow label="Status (separe por vírgula)"><Input value={draft.rdo.statusesCsv} onChange={(e) => setDraft({ ...draft, rdo: { ...draft.rdo, statusesCsv: e.target.value } })} /></FieldRow>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Campos padrão</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 gap-3">
                {Object.entries(draft.rdo.fields).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2"><Switch checked={v} onCheckedChange={(val) => setDraft({ ...draft, rdo: { ...draft.rdo, fields: { ...draft.rdo.fields, [k]: val } } })} /><span className="capitalize">{k.replace(/([A-Z])/g, " $1").toLowerCase()}</span></div>
                ))}
              </CardContent>
            </Card>
            <Card className="md:col-span-2">
              <CardHeader><CardTitle>Assinatura & Workflow</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <FieldRow label="Assinatura - tipo">
                  <Select value={draft.rdo.assinatura.tipo} onValueChange={(v) => setDraft({ ...draft, rdo: { ...draft.rdo, assinatura: { ...draft.rdo.assinatura, tipo: v as any } } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="desenho">Desenho</SelectItem>
                      <SelectItem value="certificado">Certificado</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldRow>
                <div className="flex items-center gap-2"><Switch checked={draft.rdo.assinatura.obrigatoria} onCheckedChange={(v) => setDraft({ ...draft, rdo: { ...draft.rdo, assinatura: { ...draft.rdo.assinatura, obrigatoria: v } } })} /><span>Obrigatória?</span></div>
                <div className="flex items-center gap-2"><Switch checked={draft.rdo.captureCloseDate} onCheckedChange={(v) => setDraft({ ...draft, rdo: { ...draft.rdo, captureCloseDate: v } })} /><span>Captura automática data/hora ao fechar</span></div>
                <FieldRow label="Workflow de aprovação"><Textarea value={draft.rdo.workflow} onChange={(e) => setDraft({ ...draft, rdo: { ...draft.rdo, workflow: e.target.value } })} /></FieldRow>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tarefas */}
        <TabsContent value="tarefas">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader><CardTitle>Status padrão</CardTitle></CardHeader>
              <CardContent><Input value={draft.tasks.statusesCsv} onChange={(e) => setDraft({ ...draft, tasks: { ...draft.tasks, statusesCsv: e.target.value } })} /></CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Campos de tarefa</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 gap-3">
                {Object.entries(draft.tasks.fields).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2"><Switch checked={v as boolean} onCheckedChange={(val) => setDraft({ ...draft, tasks: { ...draft.tasks, fields: { ...draft.tasks.fields, [k]: val } } })} /><span className="capitalize">{k.replace(/([A-Z])/g, " $1").toLowerCase()}</span></div>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Regras de atraso (SLA por prioridade)</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {(["baixa","media","alta"] as const).map(p => (
                  <FieldRow key={p} label={`Prioridade ${p}`}><Input type="number" value={draft.tasks.slaHoras[p]} onChange={(e) => setDraft({ ...draft, tasks: { ...draft.tasks, slaHoras: { ...draft.tasks.slaHoras, [p]: Number(e.target.value) } } })} /></FieldRow>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Kanban & Gantt</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <FieldRow label="Colunas ativas (csv)"><Input value={draft.tasks.kanbanColumnsCsv} onChange={(e) => setDraft({ ...draft, tasks: { ...draft.tasks, kanbanColumnsCsv: e.target.value } })} /></FieldRow>
                <FieldRow label="Gantt - unidade padrão">
                  <Select value={draft.tasks.ganttUnit} onValueChange={(v) => setDraft({ ...draft, tasks: { ...draft.tasks, ganttUnit: v as any } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="semana">Semana</SelectItem>
                      <SelectItem value="mes">Mês</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldRow>
                <div className="flex items-center gap-2"><Switch checked={draft.tasks.ai.nlCreate} onCheckedChange={(v) => setDraft({ ...draft, tasks: { ...draft.tasks, ai: { ...draft.tasks.ai, nlCreate: v } } })} /><span>Criação por linguagem natural</span></div>
                <div className="flex items-center gap-2"><Switch checked={draft.tasks.ai.autoSubtasks} onCheckedChange={(v) => setDraft({ ...draft, tasks: { ...draft.tasks, ai: { ...draft.tasks.ai, autoSubtasks: v } } })} /><span>Auto-subtarefas</span></div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Catálogos */}
        <TabsContent value="catalogos">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader><CardTitle>Materiais</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2"><Switch checked={draft.catalogs.materiais.enableSystemImport} onCheckedChange={(v) => setDraft({ ...draft, catalogs: { ...draft.catalogs, materiais: { ...draft.catalogs.materiais, enableSystemImport: v } } })} /><span>Habilitar importação do sistema</span></div>
                <FieldRow label="UMs padrão"><Input value={draft.catalogs.materiais.umsPadrao} onChange={(e) => setDraft({ ...draft, catalogs: { ...draft.catalogs, materiais: { ...draft.catalogs.materiais, umsPadrao: e.target.value } } })} /></FieldRow>
                <FieldRow label="Categorias"><Input value={draft.catalogs.materiais.categorias} onChange={(e) => setDraft({ ...draft, catalogs: { ...draft.catalogs, materiais: { ...draft.catalogs.materiais, categorias: e.target.value } } })} /></FieldRow>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Equipamentos</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <FieldRow label="Tipos"><Input value={draft.catalogs.equipamentos.tipos} onChange={(e) => setDraft({ ...draft, catalogs: { ...draft.catalogs, equipamentos: { ...draft.catalogs.equipamentos, tipos: e.target.value } } })} /></FieldRow>
                <FieldRow label="Manutenção - periodicidade (dias)"><Input type="number" value={draft.catalogs.equipamentos.manutencaoPeriodicidadeDias} onChange={(e) => setDraft({ ...draft, catalogs: { ...draft.catalogs, equipamentos: { ...draft.catalogs.equipamentos, manutencaoPeriodicidadeDias: Number(e.target.value) } } })} /></FieldRow>
              </CardContent>
            </Card>
            {/* O cartão de Setores/Disciplinas e a linha de Fornecedores saíram
                com as telas correspondentes na v1. As chaves `catalogs.setores`
                e `catalogs.parceiros.fornecedoresCamposMinimos` continuam no
                tipo e nos defaults de propósito: configurações já salvas no
                localStorage seguem carregando sem erro, e a v2 reaproveita. */}
            <Card>
              <CardHeader><CardTitle>Clientes</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <FieldRow label="Clientes - mínimos"><Input value={draft.catalogs.parceiros.clientesCamposMinimos} onChange={(e) => setDraft({ ...draft, catalogs: { ...draft.catalogs, parceiros: { ...draft.catalogs.parceiros, clientesCamposMinimos: e.target.value } } })} /></FieldRow>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Relatórios & Dashboards */}
        <TabsContent value="relatorios">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader><CardTitle>Indicadores padrão</CardTitle></CardHeader>
              <CardContent>
                <Textarea value={draft.reports.defaultKpis} onChange={(e) => setDraft({ ...draft, reports: { ...draft.reports, defaultKpis: e.target.value } })} placeholder="Liste os KPIs padrão separados por ponto e vírgula" />
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Layouts preferidos</CardTitle></CardHeader>
              <CardContent>
                <Input value={draft.reports.layoutsPreferidos} onChange={(e) => setDraft({ ...draft, reports: { ...draft.reports, layoutsPreferidos: e.target.value } })} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Tooltips</CardTitle></CardHeader>
              <CardContent className="flex flex-col gap-2">
                <div className="flex items-center gap-2"><Switch checked={draft.reports.tooltips.mostrarLegendaAoHover} onCheckedChange={(v) => setDraft({ ...draft, reports: { ...draft.reports, tooltips: { ...draft.reports.tooltips, mostrarLegendaAoHover: v } } })} /><span>Sempre mostrar legenda ao hover</span></div>
                <div className="flex items-center gap-2"><Switch checked={draft.reports.tooltips.mostrarValorAoHover} onCheckedChange={(v) => setDraft({ ...draft, reports: { ...draft.reports, tooltips: { ...draft.reports.tooltips, mostrarValorAoHover: v } } })} /><span>Sempre mostrar valor ao hover</span></div>
              </CardContent>
            </Card>
            <Card className="md:col-span-2">
              <CardHeader><CardTitle>Branding do relatório</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <FieldRow label="Contato"><Input value={draft.reports.branding.contato} onChange={(e) => setDraft({ ...draft, reports: { ...draft.reports, branding: { ...draft.reports.branding, contato: e.target.value } } })} /></FieldRow>
                <FieldRow label="Logo do relatório">
                  <div className="flex items-center gap-3">
                    <FilePicker onPick={(data) => setDraft({ ...draft, reports: { ...draft.reports, branding: { ...draft.reports.branding, logo: data } } })} />
                    {draft.reports.branding.logo && <img src={draft.reports.branding.logo} alt="Logo do relatório" className="h-8" />}
                  </div>
                </FieldRow>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Campos Personalizados */}
        <TabsContent value="campos">
          <Card>
            <CardHeader><CardTitle>Campos personalizados</CardTitle><CardDescription>Crie campos por módulo. (Protótipo local)</CardDescription></CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-muted-foreground">Em breve: criação completa de campos por módulo com tipos e validações.</p>
              <Button variant="outline" onClick={() => toast.message("Ação de adicionar campo (protótipo).")}>Adicionar campo</Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Segurança & Auditoria */}
        <TabsContent value="seguranca">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader><CardTitle>Política de senha & MFA</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <FieldRow label="Tamanho mínimo"><Input type="number" value={draft.security.senha.tamanhoMin} onChange={(e) => setDraft({ ...draft, security: { ...draft.security, senha: { ...draft.security.senha, tamanhoMin: Number(e.target.value) } } })} /></FieldRow>
                <FieldRow label="Força">
                  <Select value={draft.security.senha.forca} onValueChange={(v) => setDraft({ ...draft, security: { ...draft.security, senha: { ...draft.security.senha, forca: v as any } } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="baixa">Baixa</SelectItem>
                      <SelectItem value="media">Média</SelectItem>
                      <SelectItem value="alta">Alta</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldRow>
                <FieldRow label="Perfis com MFA obrigatório"><Input value={draft.security.mfaObrigatorioPerfis} onChange={(e) => setDraft({ ...draft, security: { ...draft.security, mfaObrigatorioPerfis: e.target.value } })} /></FieldRow>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>SSO & Sessão</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2"><Switch checked={draft.security.sso} onCheckedChange={(v) => setDraft({ ...draft, security: { ...draft.security, sso: v } })} /><span>SSO/OAuth</span></div>
                <FieldRow label="Tempo de sessão (min)"><Input type="number" value={draft.security.sessionTimeoutMin} onChange={(e) => setDraft({ ...draft, security: { ...draft.security, sessionTimeoutMin: Number(e.target.value) } })} /></FieldRow>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Rede & Auditoria</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <FieldRow label="Faixas de IP (CIDR)"><Textarea value={draft.security.ipRanges} onChange={(e) => setDraft({ ...draft, security: { ...draft.security, ipRanges: e.target.value } })} /></FieldRow>
                <div className="flex items-center gap-2"><Switch checked={draft.security.auditoria} onCheckedChange={(v) => setDraft({ ...draft, security: { ...draft.security, auditoria: v } })} /><span>Logs de auditoria</span></div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Backup & Retenção</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <FieldRow label="Frequência">
                  <Select value={draft.security.backup.frequencia} onValueChange={(v) => setDraft({ ...draft, security: { ...draft.security, backup: { ...draft.security.backup, frequencia: v as any } } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="diario">Diário</SelectItem>
                      <SelectItem value="semanal">Semanal</SelectItem>
                      <SelectItem value="mensal">Mensal</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldRow>
                <FieldRow label="Retenção (dias)"><Input type="number" value={draft.security.backup.retencaoDias} onChange={(e) => setDraft({ ...draft, security: { ...draft.security, backup: { ...draft.security.backup, retencaoDias: Number(e.target.value) } } })} /></FieldRow>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => toast.message("Backup gerado (simulado).")}>Gerar backup agora</Button>
                  <Button variant="outline" onClick={() => toast.message("Logs exportados (simulado).")}>Exportar logs</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
